/**
 * <flash-wheel> — Ruleta de descuentos con canvas, modal nativo y localStorage.
 *
 * Atributos:
 *   data-redirect    Ruta de destino al usar el descuento (default: /collections/packs-y-ofertas)
 *   data-section-id  ID de la sección Shopify
 *
 * Requiere dentro del elemento:
 *   <script type="application/json" data-prizes>[{label, code, weight, color}, ...]</script>
 *   [data-open-wheel]     botón que abre el modal
 *   [data-wheel-dialog]   elemento <dialog>
 *   [data-wheel-canvas]   elemento <canvas> donde se dibuja la ruleta
 *   [data-spin-btn]       botón GIRAR
 *   [data-wheel-result]   panel con el resultado (hidden por defecto)
 *   [data-result-title]   texto del premio ganado
 *   [data-result-code]    código de descuento
 *   [data-copy-btn]       botón copiar código
 *   [data-apply-link]     enlace para usar el descuento
 *   [data-already-msg]    mensaje "ya giraste hoy" (hidden por defecto)
 *   [data-close-btn]      botón cerrar modal
 */
class FlashWheel extends HTMLElement {
  connectedCallback() {
    const prizesEl = this.querySelector('[data-prizes]');
    if (!prizesEl) return;

    try {
      this.prizes = JSON.parse(prizesEl.textContent);
    } catch (e) {
      return;
    }

    if (!this.prizes || this.prizes.length < 2) return;

    this.redirect = this.getAttribute('data-redirect') || '/collections/packs-y-ofertas';
    this.dialog = this.querySelector('[data-wheel-dialog]');
    this.canvas = this.querySelector('[data-wheel-canvas]');
    this.spinBtn = this.querySelector('[data-spin-btn]');
    this.resultPanel = this.querySelector('[data-wheel-result]');
    this.resultTitle = this.querySelector('[data-result-title]');
    this.resultCode = this.querySelector('[data-result-code]');
    this.copyBtn = this.querySelector('[data-copy-btn]');
    this.applyLink = this.querySelector('[data-apply-link]');
    this.alreadyMsg = this.querySelector('[data-already-msg]');
    this.openBtn = this.querySelector('[data-open-wheel]');

    this.rotation = 0;
    this.isSpinning = false;

    this.drawWheel();
    this.setupEvents();
  }

  setupEvents() {
    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.open());
    }

    const closeBtn = this.querySelector('[data-close-btn]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.dialog.close());
    }

    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
    }

    if (this.spinBtn) {
      this.spinBtn.addEventListener('click', () => this.spin());
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => this.copyCode());
    }
  }

  open() {
    if (!this.dialog) return;
    const saved = this.getTodaySave();
    if (saved) {
      this.showResult(saved, true);
    } else {
      if (this.resultPanel) this.resultPanel.hidden = true;
      if (this.alreadyMsg) this.alreadyMsg.hidden = true;
      if (this.spinBtn) {
        this.spinBtn.disabled = false;
        this.spinBtn.hidden = false;
      }
    }
    this.dialog.showModal();
  }

  getTodayKey() {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  }

  getTodaySave() {
    // En modo diseño Shopify, permitir girar ilimitado para probar
    if (window.Shopify && window.Shopify.designMode) return null;
    try {
      const raw = localStorage.getItem('activalab-flash-wheel-cyber26');
      if (!raw) return null;
      const data = JSON.parse(raw);
      return data.date === this.getTodayKey() ? data : null;
    } catch (e) {
      return null;
    }
  }

  saveTodayPrize(prize) {
    try {
      localStorage.setItem('activalab-flash-wheel-cyber26', JSON.stringify({
        date: this.getTodayKey(),
        code: prize.code,
        label: prize.label,
      }));
    } catch (e) {}
  }

  selectPrize() {
    const total = this.prizes.reduce((s, p) => s + (p.weight || 1), 0);
    let r = Math.random() * total;
    for (const prize of this.prizes) {
      r -= prize.weight || 1;
      if (r <= 0) return prize;
    }
    return this.prizes[this.prizes.length - 1];
  }

  spin() {
    if (this.isSpinning) return;
    this.isSpinning = true;
    if (this.spinBtn) this.spinBtn.disabled = true;

    const winner = this.selectPrize();
    const N = this.prizes.length;
    const segAngle = 360 / N;
    const winnerIdx = this.prizes.indexOf(winner);
    const centerAngle = (winnerIdx + 0.5) * segAngle;
    const jitter = (Math.random() - 0.5) * segAngle * 0.5;
    const turns = 4 + Math.floor(Math.random() * 3);
    const targetRotation = this.rotation + turns * 360 + (360 - centerAngle) + jitter;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      this.rotation = targetRotation;
      if (this.canvas) this.canvas.style.transform = `rotate(${this.rotation % 360}deg)`;
      this.finishSpin(winner);
      return;
    }

    if (this.canvas) {
      this.canvas.style.transition = 'transform 4.5s cubic-bezier(0.2, 0.8, 0.1, 1)';
      this.canvas.style.transform = `rotate(${targetRotation}deg)`;
    }
    this.rotation = targetRotation;

    const onEnd = () => {
      if (this.canvas) this.canvas.removeEventListener('transitionend', onEnd);
      this.finishSpin(winner);
    };
    if (this.canvas) this.canvas.addEventListener('transitionend', onEnd);
    // Fallback si transitionend no dispara
    setTimeout(() => {
      if (this.canvas) this.canvas.removeEventListener('transitionend', onEnd);
      if (this.isSpinning) this.finishSpin(winner);
    }, 5200);
  }

  finishSpin(winner) {
    this.isSpinning = false;
    this.saveTodayPrize(winner);
    this.showResult(winner, false);
  }

  showResult(prize, alreadyPlayed) {
    if (this.spinBtn) this.spinBtn.hidden = true;
    if (this.resultPanel) this.resultPanel.hidden = false;
    if (this.alreadyMsg) this.alreadyMsg.hidden = !alreadyPlayed;
    if (this.resultTitle) this.resultTitle.textContent = prize.label;
    if (this.resultCode) this.resultCode.textContent = prize.code;
    if (this.applyLink) {
      this.applyLink.href = `/discount/${encodeURIComponent(prize.code)}?redirect=${encodeURIComponent(this.redirect)}`;
    }
  }

  copyCode() {
    const code = this.resultCode ? this.resultCode.textContent : '';
    const originalLabel = this.copyBtn ? this.copyBtn.textContent : '';

    const done = () => {
      if (this.copyBtn) {
        this.copyBtn.textContent = '¡Copiado!';
        setTimeout(() => { if (this.copyBtn) this.copyBtn.textContent = originalLabel; }, 2000);
      }
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(done).catch(() => this.fallbackCopy(code, done));
    } else {
      this.fallbackCopy(code, done);
    }
  }

  fallbackCopy(text, callback) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      callback();
    } catch (e) {}
  }

  drawWheel() {
    if (!this.canvas || !(this.canvas instanceof HTMLCanvasElement)) return;
    const N = this.prizes.length;
    if (N < 2) return;

    const size = this.canvas.width;
    const ctx = this.canvas.getContext('2d');
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 4;
    const segAngle = (2 * Math.PI) / N;
    const palette = ['#dc1986', '#1a1a1a', '#b01070', '#FFB800', '#7a00d6', '#e65100', '#005f8f', '#2d6a4f'];

    for (let i = 0; i < N; i++) {
      const startAngle = i * segAngle - Math.PI / 2;
      const endAngle = startAngle + segAngle;
      const color = (this.prizes[i].color && this.prizes[i].color !== '') ? this.prizes[i].color : palette[i % palette.length];

      // Segmento
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();

      // Borde del segmento
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, endAngle);
      ctx.closePath();
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Etiqueta
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(startAngle + segAngle / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.max(10, Math.floor(size / 22))}px sans-serif`;
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 4;
      // maxWidth: desde el borde hasta justo fuera del círculo central, para que etiquetas largas no se monten
      ctx.fillText(this.prizes[i].label, r - 10, 5, r - 10 - size * 0.13);
      ctx.restore();
    }

    // Círculo central decorativo
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.1, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#dc1986';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
}

if (!customElements.get('flash-wheel')) {
  customElements.define('flash-wheel', FlashWheel);
}
